import { Table, TableProps } from "antd";
import React, { Key, useEffect, useMemo } from "react";
import { useGetEmittedTitlesListQuery } from "../../../redux/api/reconciliationsApiSlice";
import { EmittedTitle } from "../../../types/reconciliation";
import {
  getColumnDateProps,
  getNumberRangeFilter,
  getStandardFilter
} from "../../tables/negotiationsTables/columnsUtils";
import { useEmittedTitlesTable } from "../../../context/EmittedTitleTableContext";
import { isEmpty } from "lodash";
import FilterBySearch from "../../tables/negotiationsTables/generalTable/FilterBySearch";
import { printValue } from "utils/reconciliationTableUtils";
import BrokerFilter from "../../tables/negotiationsTables/BrokerFilter";

export default function EmittedTitlesTable() {

  const {
    page,
    pageSize,
    ordering,
    collectiveContractCode,
    confirmed,
    contractor,
    contractCode,
    credit,
    debit,
    documentName,
    productCode,
    startDate,
    taxableAmount,
    movementCode,
    brokerName,
    setPage,
    setPageSize,
    setOrdering,
    setCollectiveContractCode,
    setConfirmed,
    setContractCode,
    setContractor,
    setCredit,
    setDebit,
    setDocumentName,
    setProductCode,
    setStartDate,
    setTaxableAmount,
    setMovementCode,
    setBrokerName
  } = useEmittedTitlesTable();

  const {
    data,
    isUninitialized,
    isFetching
  } = useGetEmittedTitlesListQuery({
    page,
    page_size: pageSize,
    ...(!isEmpty(ordering) && { ordering: ordering }),
    //@ts-ignore
    ...(!isEmpty(credit) && {
      credit_min: credit?.at(0) as string,
      credit_max: credit?.at(1) as string
    }),
    ...(!isEmpty(debit) && {
      credit_min: debit?.at(0) as string,
      credit_max: debit?.at(1) as string
    }),
    ...(!isEmpty(taxableAmount) && {
      taxable_amount_min: taxableAmount?.at(0) as string,
      taxable_amount_max: taxableAmount?.at(1) as string
    }),
    ...(!isEmpty(documentName) && { document_name: documentName?.join(",") }),
    //@ts-ignore
    ...(!isEmpty(brokerName) && { broker: brokerName?.map(broker => broker.value).join(",") }),
    ...(!isEmpty(movementCode) && { movement_code: movementCode?.join(",") }),
    ...(!isEmpty(productCode) && { product_code: productCode?.join(",") }),
    ...(!isEmpty(contractCode) && { contract_code: contractCode?.join(",") }),
    ...(!isEmpty(collectiveContractCode) && { collective_contract_code: collectiveContractCode?.join(",") }),
    ...(!isEmpty(contractor) && { contractor: contractor?.join(",") }),
    ...(!isEmpty(startDate) && {
      start_date_after: startDate?.at(0) as string,
      start_date_before: startDate?.at(1) as string
    }),
    ...(!isEmpty(confirmed) && { confirmed: confirmed?.join(",") })
  } ?? {});
  const columns: TableProps<EmittedTitle>["columns"] = useMemo(() => [
    {
      key: "document_name",
      dataIndex: "document_name",
      title: "Nome Documento",
      filteredValue: documentName,
      sorter: { multiple: 1 },
      filterDropdown: (props) => {
        return <FilterBySearch
          props={props}
          placeholder={"Filtra per nome documento..."} />;
      },
      ellipsis: true,
      render: (value) => printValue(value, "text")
    },
    {
      key: "confirmed",
      dataIndex: "confirmed",
      title: "In Rimessa",
      filteredValue: confirmed,
      sorter: { multiple: 1 },
      width: 140,
      ellipsis: true,
      render: (value: any) => {
        return <>
          {value ? "Sì" : "No"}
        </>;
      },
      ...getStandardFilter([{ text: "Sì", value: true }, { text: "No", value: false }])
    },
    {
      key: "movement_code",
      dataIndex: "movement_code",
      title: "Codice movimento",
      filteredValue: movementCode,
      sorter: { multiple: 1 },
      filterDropdown: (props) => {
        return <FilterBySearch
          props={props}
          placeholder={"Filtra per codice movimento..."} />;
      },
      ellipsis: true,
      render: (value) => printValue(value, "text")
    },
    {
      key: "broker_name",
      dataIndex: "broker_name",
      title: "Broker",
      sorter: { multiple: 1 },
      ellipsis: true,
      filteredValue: brokerName,
      filterDropdown: (props) => BrokerFilter({ ...props, isReconciliationBroker: true }),
      render: (value) => printValue(value, "text")
    },
    {
      key: "collective_contract_code",
      dataIndex: "collective_contract_code",
      title: "Numero polizza",
      filteredValue: collectiveContractCode,
      sorter: { multiple: 1 },
      filterDropdown: (props) => {
        return <FilterBySearch
          props={props}
          placeholder={"Filtra per noumero polizza..."} />;
      },
      ellipsis: true,
      render: (value) => printValue(value, "text")
    },
    {
      key: "contract_code",
      dataIndex: "contract_code",
      title: "Numero certificato",
      filteredValue: contractCode,
      sorter: { multiple: 1 },
      filterDropdown: (props) => {
        return <FilterBySearch
          props={props}
          placeholder={"Filtra per noumero certificato..."} />;
      },
      ellipsis: true,
      render: (value) => printValue(value, "text")
    },
    {
      key: "product_code",
      dataIndex: "product_code",
      title: "Codice prodotto",
      filteredValue: productCode,
      sorter: { multiple: 1 },
      filterDropdown: (props) => {
        return <FilterBySearch
          props={props}
          placeholder={"Filtra per codice prodotto..."} />;
      },
      ellipsis: true,
      render: (value) => printValue(value, "text")
    },
    {
      key: "contractor",
      dataIndex: "contractor",
      title: "Contraente",
      sorter: { multiple: 1 },
      filteredValue: contractor,
      ellipsis: true,
      filterDropdown: (props) => {
        return <FilterBySearch
          props={props}
          placeholder={"Filtra per il nome del contraente..."} />;
      },
      render: (value) => printValue(value, "text")
    },
    {
      key: "start_date",
      dataIndex: "start_date",
      title: "Data inizio",
      sorter: { multiple: 1 },
      filteredValue: startDate,
      ...getColumnDateProps("start_date", "day"),
      ellipsis: true,
      render: (value, record) => printValue(value, "date", { dateTemplate: "DD/MM/YYYY" })
    },
    {
      key: "taxable_amount",
      dataIndex: "taxable_amount",
      title: "Importo imponibile",
      className: "justify-right",
      sorter: { multiple: 1 },
      filteredValue: taxableAmount,
      ...getNumberRangeFilter(0, 1000000, 0.1, "euro"),
      ellipsis: true,
      render: (value) => printValue(value, "currency")
    },
    {
      key: "credit",
      dataIndex: "credit",
      title: "Premio lordo",
      className: "justify-right",
      sorter: { multiple: 1 },
      filteredValue: credit,
      ...getNumberRangeFilter(0, 1000000, 0.1, "euro"),
      ellipsis: true,
      render: (value) => printValue(value, "currency")
    },
    {
      key: "debit",
      dataIndex: "debit",
      title: "Provvigioni",
      className: "justify-right",
      sorter: { multiple: 1 },
      filteredValue: debit,
      ...getNumberRangeFilter(0, 1000000, 0.1, "euro"),
      ellipsis: true,
      render: (value) => printValue(value, "currency")
    }
  ], [brokerName, collectiveContractCode, confirmed, contractCode, contractor, credit, debit, documentName, movementCode, productCode, startDate, taxableAmount]);

  return <Table
    <EmittedTitle>
    columns={columns}
    dataSource={data?.results}
    loading={isFetching || isUninitialized}
    rowKey={"uuid"}
    scroll={{ x: true }}
    pagination={{
      current: page,
      pageSize: pageSize,
      showSizeChanger: true,
      total: data?.count ?? 0,
      showTotal: (total, range) => `${range[0]}-${range[1]} di ${total} titoli`,
      onChange: (page, pageSize) => {
        setPage(page);
        setPageSize(pageSize);
      }
    }}
    onChange={(_, filters, sorter) => {
      // Gestione ordinamento
      const buildOrderingString = (sorterValue: any): string => {
        if (Array.isArray(sorterValue)) {
          return sorterValue
            .map(s => (s.order === "descend" ? `-${s.field}` : `${s.field}`))
            .join(",");
        } else if (sorterValue.order) {
          return sorterValue.order === "descend" ? `-${sorterValue.field}` : `${sorterValue.field}`;
        }
        return "";
      };

      // Applicazione ordinamento
      setOrdering(buildOrderingString(sorter));

      // Gestione filtri
      if (filters) {
        // Mappa delle funzioni setter per ogni tipo di filtro
        const filterSetters: Record<string, (value: Key[]) => void> = {
          collective_contract_code: setCollectiveContractCode,
          confirmed: setConfirmed,
          contract_code: setContractCode,
          contractor: setContractor,
          credit: setCredit,
          debit: setDebit,
          document_name: setDocumentName,
          product_code: setProductCode,
          start_date: setStartDate,
          taxable_amount: setTaxableAmount,
          movement_code: setMovementCode,
          broker_name: setBrokerName
        };

        // Applica i setter per ogni filtro
        Object.entries(filterSetters).forEach(([filterName, setter]) => {
          const filterValue = filters[filterName];
          setter(!isEmpty(filterValue) ? filterValue as Key[] : []);
        });
      }
    }}
  />;
}